import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon, EnvelopeOpenIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import PasswordChecklist from 'react-password-checklist';
import { Button } from '../../components/Button';
import { useForgotPassword } from '../../hooks/auth/useForgotPassword';

export interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalStep = 'email' | 'code-password' | 'success';

export default function ForgotPasswordModal({
  isOpen,
  onClose,
  onSuccess,
}: ForgotPasswordModalProps) {
  const [step, setStep] = useState<ModalStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');

  const forgotPassword = useForgotPassword();

  const resetModal = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordValid(false);
    setErrorMessage('');
    setResendMessage('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setErrorMessage('');
    const result = await forgotPassword.requestResetCode(email);

    if (result.success) {
      setStep('code-password');
    } else {
      setErrorMessage(result.error || 'Failed to send code. Please try again.');
    }
  };

  const handleResendCode = async () => {
    setResendMessage('');
    setErrorMessage('');
    const result = await forgotPassword.requestResetCode(email);

    if (result.success) {
      setResendMessage('Code sent!');
    } else {
      setResendMessage('Failed to resend. Please try again.');
    }
  };

  const handleResetPassword = async () => {
    if (!code || code.length !== 6) {
      setErrorMessage('Please enter the 6-digit code');
      return;
    }

    if (!passwordValid) {
      setErrorMessage('Please ensure your password meets all requirements');
      return;
    }

    setErrorMessage('');
    const result = await forgotPassword.confirmNewPassword(email, code, newPassword);

    if (result.success) {
      setStep('success');
    } else {
      setErrorMessage(result.error || 'Failed to reset password. Please try again.');
    }
  };

  const handleSuccessClose = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-surface px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div
                    className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${
                      step === 'success'
                        ? 'bg-success-subtle'
                        : step === 'code-password'
                          ? 'bg-subtle'
                          : 'bg-subtle'
                    }`}
                  >
                    {step === 'success' ? (
                      <CheckIcon className="h-8 w-8 text-success" aria-hidden="true" />
                    ) : step === 'code-password' ? (
                      <LockClosedIcon className="h-8 w-8 text-brand" aria-hidden="true" />
                    ) : (
                      <EnvelopeOpenIcon className="h-8 w-8 text-brand" aria-hidden="true" />
                    )}
                  </div>
                  <div className="mt-4 text-center sm:mt-6">
                    <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-main">
                      {step === 'success'
                        ? 'Password Reset Complete'
                        : step === 'code-password'
                          ? 'Enter Reset Code'
                          : 'Reset Your Password'}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-muted">
                        {step === 'success'
                          ? 'Your password has been successfully reset. You can now log in with your new password.'
                          : step === 'code-password'
                            ? `We've sent a 6-digit code to ${email}. Enter it below along with your new password.`
                            : "Enter your email address and we'll send you a code to reset your password."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 1: Email Input */}
                {step === 'email' && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="reset-email" className="block text-sm font-bold text-main">
                        Email address
                      </label>
                      <div className="mt-2">
                        <input
                          id="reset-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          disabled={forgotPassword.isProcessing}
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrorMessage('');
                          }}
                          className="block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {errorMessage && <p className="text-sm font-bold text-error">{errorMessage}</p>}

                    <Button
                      type="button"
                      variant="primary"
                      fullWidth
                      onClick={handleSendCode}
                      disabled={forgotPassword.isProcessing || !email}
                      loading={forgotPassword.isProcessing}
                    >
                      {forgotPassword.isProcessing ? 'Sending...' : 'Send Reset Code'}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      onClick={handleClose}
                      disabled={forgotPassword.isProcessing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                {/* Step 2: Code + Password Input */}
                {step === 'code-password' && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="reset-code" className="block text-sm font-bold text-main">
                        Verification Code
                      </label>
                      <div className="mt-2">
                        <input
                          id="reset-code"
                          type="text"
                          className="block w-full rounded-2xl border-0 bg-subtle py-3 text-center text-2xl font-bold tracking-[0.5em] text-main shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-teal-600 transition-all placeholder-text-disabled"
                          placeholder="000000"
                          maxLength={6}
                          value={code}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setCode(val);
                            setErrorMessage('');
                          }}
                        />
                      </div>
                      <div className="mt-2 text-center">
                        <p className="text-sm font-medium text-muted">
                          No code?{' '}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleResendCode}
                            disabled={forgotPassword.isProcessing}
                            className="hover:underline p-0 h-auto"
                          >
                            Send again
                          </Button>
                        </p>
                        {resendMessage && (
                          <p
                            className={`mt-1 text-xs font-bold ${
                              resendMessage.includes('Failed') ? 'text-error' : 'text-success'
                            }`}
                          >
                            {resendMessage}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="new-password" className="block text-sm font-bold text-main">
                        New Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="new-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          disabled={forgotPassword.isProcessing}
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            setErrorMessage('');
                          }}
                          className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
                            newPassword.length === 0 || passwordValid
                              ? ''
                              : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
                          }`}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-new-password"
                        className="block text-sm font-bold text-main"
                      >
                        Confirm New Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="confirm-new-password"
                          type="password"
                          autoComplete="new-password"
                          required
                          disabled={forgotPassword.isProcessing}
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            setErrorMessage('');
                          }}
                          className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
                            newPassword.length === 0 || passwordValid
                              ? ''
                              : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
                          }`}
                        />
                      </div>
                    </div>

                    {newPassword.length > 0 && (
                      <div className="mt-4">
                        <PasswordChecklist
                          rules={['minLength', 'specialChar', 'number', 'capital', 'match']}
                          minLength={5}
                          value={newPassword}
                          valueAgain={confirmPassword}
                          onChange={setPasswordValid}
                          style={{ fontSize: '0.875rem' }}
                          className="font-medium text-muted"
                        />
                      </div>
                    )}

                    {errorMessage && <p className="text-sm font-bold text-error">{errorMessage}</p>}

                    <Button
                      type="button"
                      variant="primary"
                      fullWidth
                      onClick={handleResetPassword}
                      disabled={forgotPassword.isProcessing || !passwordValid || !code}
                      loading={forgotPassword.isProcessing}
                    >
                      {forgotPassword.isProcessing ? 'Resetting...' : 'Reset Password'}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      fullWidth
                      onClick={handleClose}
                      disabled={forgotPassword.isProcessing}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                {/* Step 3: Success */}
                {step === 'success' && (
                  <div className="mt-6 sm:mt-8">
                    <Button type="button" variant="primary" fullWidth onClick={handleSuccessClose}>
                      Continue to Login
                    </Button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
